import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { BarChart3, Table as TableIcon } from "lucide-react";
import { nb } from "@/lib/format";
import { kFmt, ChartTooltip } from "@/components/finance/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

export function ResultTab({ data, months, totals, barData, year, setYear, view, setView, colors }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-headings text-base font-bold tracking-tight">Inntekter, kostnader og resultat</h3>
        <div className="flex items-center gap-2">
          <ToggleGroup
            type="single" value={year ? String(year) : ""} data-testid="year-toggle"
            onValueChange={(v) => v && setYear(Number(v))}
            className="rounded-lg bg-muted p-1"
          >
            {(data?.available_years || []).map((y) => (
              <ToggleGroupItem key={y} value={String(y)} data-testid={`year-${y}`}
                className="h-7 px-3 text-xs data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow">
                {y}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <ToggleGroup
            type="single" value={view} data-testid="view-toggle"
            onValueChange={(v) => v && setView(v)}
            className="rounded-lg bg-muted p-1"
          >
            <ToggleGroupItem value="chart" data-testid="view-chart" className="h-7 w-8 p-0 data-[state=on]:bg-background data-[state=on]:shadow">
              <BarChart3 className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="table" data-testid="view-table" className="h-7 w-8 p-0 data-[state=on]:bg-background data-[state=on]:shadow">
              <TableIcon className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {!data ? (
        <Skeleton className="h-[360px] w-full rounded-xl" />
      ) : view === "chart" ? (
        <div className="h-[360px] w-full" data-testid="resultat-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: colors.grid }} tick={{ fill: colors.axis, fontSize: 12 }} />
              <YAxis tickFormatter={kFmt} tickLine={false} axisLine={false} tick={{ fill: colors.axis, fontSize: 12 }} width={48} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: colors.grid, opacity: 0.3 }} />
              <Bar dataKey="Inntekter" fill={colors.inntekter} radius={[3, 3, 0, 0]} maxBarSize={22} />
              <Bar dataKey="Kostnader" fill={colors.kostnader} radius={[3, 3, 0, 0]} maxBarSize={22} />
              <Bar dataKey="Resultat" fill={colors.resultat} radius={[3, 3, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="resultat-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Måned</th>
                <th className="py-2 pr-4 text-right font-medium">Inntekter</th>
                <th className="py-2 pr-4 text-right font-medium">Kostnader</th>
                <th className="py-2 text-right font-medium">Resultat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {months.filter((m) => m.inntekter !== null).map((m) => (
                <tr key={m.month} data-testid={`row-${m.month}`}>
                  <td className="py-2 pr-4 font-medium">{m.month}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{nb(m.inntekter)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">{nb(m.kostnader)}</td>
                  <td className={`py-2 text-right font-semibold tabular-nums ${m.resultat < 0 ? "text-destructive" : ""}`}>{nb(m.resultat)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Totals footer */}
      <div className="mt-6 grid grid-cols-3 gap-3 border-t border-border/60 pt-5">
        {[
          ["INNTEKTER", totals.inntekter, colors.inntekter, "total-inntekter"],
          ["KOSTNADER", totals.kostnader, colors.kostnader, "total-kostnader"],
          ["RESULTAT", totals.resultat, colors.resultat, "total-resultat"],
        ].map(([label, val, color, tid]) => (
          <div key={label} className="text-center" data-testid={tid}>
            <div className="mx-auto mb-2 h-1 w-10 rounded-full" style={{ background: color }} />
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className="mt-1 font-headings text-lg font-extrabold tabular-nums sm:text-2xl">{val != null ? nb(val) : "–"}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
